import { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { SearchInput } from "@/components/SearchInput";
import { PaginationBar } from "@/components/Pagination";
import { Loading, TableSkeleton } from "@/components/Loading";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";

// Reusable table with search, pagination and loading/empty/error states.
// columns: [{ key, header, render?(row), className? }]
export function DataTable({
  columns,
  data = [],
  loading = false,
  error = null,
  onRetry,
  searchable = true,
  searchKeys = [],
  searchPlaceholder = "Sök…",
  emptyTitle = "Inga rader",
  emptyDescription,
  pageSize = 8,
  testId = "data-table",
}) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (!query) return data;
    const q = query.toLowerCase();
    const keys = searchKeys.length ? searchKeys : columns.map((c) => c.key);
    return data.filter((row) =>
      keys.some((k) => String(row[k] ?? "").toLowerCase().includes(q))
    );
  }, [data, query, searchKeys, columns]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const rows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="flex flex-col gap-4" data-testid={testId}>
      {searchable && (
        <SearchInput
          value={query}
          onChange={(v) => {
            setQuery(v);
            setPage(1);
          }}
          placeholder={searchPlaceholder}
          testId={`${testId}-search`}
        />
      )}

      <Card className="overflow-hidden rounded-xl border-border">
        {loading ? (
          <TableSkeleton rows={8} cols={columns.length} />
        ) : error ? (
          <ErrorState description={error} onRetry={onRetry} />
        ) : rows.length === 0 ? (
          <EmptyState title={emptyTitle} description={emptyDescription} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-b bg-muted/40 hover:bg-muted/40">
                {columns.map((c) => (
                  <TableHead key={c.key} className={c.className}>
                    {c.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={row.id || i} data-testid={`${testId}-row`} className="transition-colors hover:bg-muted/40">
                  {columns.map((c) => (
                    <TableCell key={c.key} className={c.className}>
                      {c.render ? c.render(row) : row[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {!loading && !error && filtered.length > 0 && (
        <PaginationBar page={safePage} pageCount={pageCount} onPageChange={setPage} total={filtered.length} />
      )}
    </div>
  );
}
